<template>
  <div class="row">
    <!-- Left Column: Customer Details -->
    <div class="col-md-6">
      <div class="row">
        <div class="col">
          <kt-input 
            v-model="model.Company" 
            label="Company Name" 
            placeholder="Enter company name"
          ></kt-input>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col">
          <kt-input 
            v-model="model.Code" 
            label="Company Code" 
            placeholder="Enter company code"
          ></kt-input>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col">
          <kt-input 
            type="textarea" 
            v-model="model.ContactInformation" 
            label="Contact Information" 
            placeholder="Enter company contact information"
          ></kt-input>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col">
          <kt-input 
            type="text" 
            v-model="model.Phone" 
            label="Main Phone" 
            placeholder="Enter company phone"
          ></kt-input>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col">
          <div class="form-check">
            <input
              type="checkbox"
              class="form-check-input"
              v-model="model.Status"
              :true-value="1"
              :false-value="0"
              id="active-check"
            >
            <label 
              class="form-check-label" 
              for="active-check">
              Is Active?
            </label>
          </div>
        </div>
      </div>

      <!-- Profile Image Section -->
      <div class="row mt-4" v-if="model.Id">
        <div class="col">
          <h6 class="text-muted mb-2">Profile Image</h6>
          
          <!-- Loading state -->
          <div v-if="imageLoading" class="text-center py-4">
            <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
            <p class="text-muted mt-2">Loading image...</p>
          </div>
          
          <!-- Display existing image -->
          <div v-else-if="profileImage" class="mb-3">
            <img 
              :src="imageUrl" 
              alt="Profile Image"
              class="img-thumbnail"
              style="max-width: 200px; max-height: 200px; object-fit: cover;"
            />
            <div class="mt-2">
              <button 
                @click="deleteImage()" 
                class="btn btn-sm btn-outline-danger me-2"
              >
                <i class="fa fa-trash"></i> Delete Image
              </button>
              <label class="btn btn-sm btn-outline-primary">
                <i class="fa fa-upload"></i> Replace Image
                <input 
                  type="file" 
                  @change="handleImageUpload($event)" 
                  accept="image/*"
                  style="display: none;"
                />
              </label>
            </div>
          </div>

          <!-- Upload new image -->
          <div v-else>
            <label class="btn btn-sm btn-outline-primary">
              <i class="fa fa-upload"></i> Upload Profile Image
              <input 
                type="file" 
                @change="handleImageUpload($event)" 
                accept="image/*"
                style="display: none;"
              />
            </label>
            <p class="text-muted small mt-2">Supported formats: JPG, PNG, GIF (max 5MB)</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Staff/Contacts -->
    <div class="col-md-6">
      <div class="card h-100">
        <div class="card-header bg-light">
          <h6 class="mb-0">
            <i class="fas fa-users me-2"></i>Staff / Contacts
          </h6>
        </div>
        <div class="card-body">
          <div v-if="!model.Id" class="text-muted fst-italic">
            Save the customer first to add contacts.
          </div>
          <div v-else>
            <!-- Staff Loading -->
            <div v-if="staffLoading" class="text-center py-3">
              <i class="fas fa-spinner fa-spin text-primary"></i>
              <span class="ms-2">Loading contacts...</span>
            </div>
            
            <!-- Staff List -->
            <div v-else>
              <ul class="list-group list-group-flush mb-3" v-if="staff.length">
                <li 
                  v-for="row in staff" 
                  :key="row.Id"
                  class="list-group-item px-0 d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>{{ row.Name }}</strong>
                    <br>
                    <small class="text-muted">{{ row.Email }}</small>
                  </div>
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary" @click="staffEdit(row)">
                      <i class="fa fa-edit"></i>
                    </button>
                    <button class="btn btn-outline-danger" @click="staffDelete(row)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </li>
              </ul>
              <div v-else class="text-muted fst-italic mb-3">
                No contacts found for this customer.
              </div>
              
              <button class="btn btn-sm btn-outline-primary" @click="addStaff()">
                <i class="fas fa-plus me-1"></i> Add Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Custom Contact Form -->
        <kt-modal
          :title="staffListTitle"
          :visible="staffListVisible"
          @dismiss="staffListDismiss()"
        >
          <template #body>
          <!-- Customer Information Header -->
          <div class="card mb-3 bg-light border-primary" v-if="toggled">
            <div class="card-body p-3">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="mb-2 text-primary">
                    <i class="fas fa-building"></i> {{ toggled.Company || 'N/A' }}
                  </h6>
                  <p class="mb-1 small">
                    <strong>Code:</strong> {{ toggled.Code || 'N/A' }}
                  </p>
                  <p class="mb-0 small" v-if="toggled.Phone">
                    <strong>Phone:</strong> {{ toggled.Phone }}
                  </p>
                </div>
                <div class="col-md-6">
                  <p class="mb-1 small">
                    <strong>Customer #:</strong> {{ toggled.CustomerNumber || 'N/A' }}
                  </p>
                  <p class="mb-0 small" v-if="toggled.ContactInformation">
                    <strong>Address:</strong><br>
                    <span style="white-space: pre-line;">{{ toggled.ContactInformation }}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Contact List -->
          <h6 class="mb-2"><strong>Contacts</strong></h6>
          <ul class="list-group">
            <li v-for="row in staff"
                class="list-group-item"
            >
              <div class="row">
                <div class="col-8">
                  <small>{{ row.Name }} : {{ row.Email }}</small>
                </div>
                <div class="col-4 text-end">
                  <div class="-btn-group">
                    <button class="btn btn-sm btn-outline-danger" @click="staffDelete(row)">
                      <i class="fa fa-trash"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" @click="staffEdit(row)">
                      <i class="fa fa-edit"></i>
                    </button>
                  </div>
                </div>
              </div>
            </li>
            <li class="list-group-item" v-if="!staff.length">
              <small><em>No Contacts Found</em></small>
            </li>
          </ul>
          <button class="btn btn-outline-secondary btn-sm mt-3" @click="addStaff()">
            <!-- <i class="fa fa-add"></i> -->
            <span>Add Contact</span>
          </button>
          </template>
        </kt-modal>
        <kt-modal
            :title="staffTitle"
            :visible="staffVisible"
            @dismiss="staffDismiss()"

        >
            <template #body>
                <staff-form v-model="staffForm"></staff-form>
            </template>

            <template #footer>
                <div class="container-fluid px-0">
                    <div class="row">
                        <div class="col">
                            <button 
                                @click="staffDismiss()" 
                                class="btn btn-sm btn-outline-secondary form-control"
                            >Close</button>
                        </div>
                        <div class="col">
                            <button 
                                @click="staffSave(staffForm)" 
                                class="btn btn-sm btn-outline-primary form-control"
                            >Save</button>
                        </div>
                    </div>
                </div>
            </template>

        </kt-modal>
    
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useRoute } from "vue-router"
import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import Alert from "@/services/Alert";
import KtInput from "@/components/UI/KtInput.vue";
import { getApiBaseUrl } from "@/services/Http";
import KtModal from "@/components/UI/KtModal.vue";
import StaffForm from "@/components/Staff/StaffForm.vue";

const route = useRoute();

const model = defineModel({
    type: Object,
    default: {}
});

// Profile Image State
const profileImage = ref(null);
const imageLoading = ref(true); // Start as true to show spinner initially
const imageUrl = computed(() => {
    return profileImage.value ? `${getApiBaseUrl()}/Data/Download/${profileImage.value.Id}` : '';
});

// Staff/Contacts State
const staff = ref([]);
const staffLoading = ref(false);
const staffVisible = ref(false);
const staffTitle = ref('Add Contact');
const staffForm = ref({});
const staffListVisible = ref(false);
const staffListTitle = ref('Contacts');
const toggled = ref(null);

const loadProfileImage = async () => {
    if (!model.value.Id) {
        imageLoading.value = false;
        return;
    }
    
    imageLoading.value = true;
    try {
        const response = await ApiService.loadContactImage(model.value.Id);
        profileImage.value = response || null;
    } catch (error) {
        console.error('Error loading profile image:', error);
        profileImage.value = null;
    } finally {
        imageLoading.value = false;
    }
};

// Staff Management
const loadStaff = async () => {
    if (!model.value.Id || !model.value.CustomerNumber) return;
    
    staffLoading.value = true;
    try {
        const response = await Api.loadStaff({ 
            CustomerNo: [model.value.CustomerNumber],
            Page: 0,
            Size: 999999999
        }, true);
        // API returns data directly, not wrapped
        staff.value = response ?? [];
    } catch (error) {
        console.error('Error loading staff:', error);
        staff.value = [];
    } finally {
        staffLoading.value = false;
    }
};

// Load profile image when model.Id changes
watch(() => model.value.Id, async (newId) => {
    if (newId) {
        await loadProfileImage();
        await loadStaff();
    } else {
        imageLoading.value = false;
    }
}, { immediate: true });

const addStaff = () => {
    staffForm.value = { ClientMap: [model.value.CustomerNumber] };
    staffTitle.value = 'Add Contact';
    staffVisible.value = true;
};

const staffEdit = async (row) => {
    // Load full staff details including ClientMap
    const staff = await Api.loadStaffById(row.Id, true);
    
    // Transform ClientMap to extract ClientRef values for the multi-select
    let cm = [];
    if (staff.ClientMap) {
        staff.ClientMap.forEach((val) => {
            cm.push(val.ClientRef);
        });
    }
    staff.ClientMap = cm;
    
    staffForm.value = staff;
    staffTitle.value = 'Edit Contact';
    staffVisible.value = true;
};

const staffDismiss = () => {
    staffVisible.value = false;
    staffForm.value = {};
};

const staffSave = async (formData) => {
    try {
        await Api.saveStaff(formData, true);
        Alert.notify({ text: 'Contact saved successfully!', type: 'success' });
        staffDismiss();
        await loadStaff();
    } catch (error) {
        console.error('Error saving staff:', error);
        Alert.notify({ text: 'Error saving contact', type: 'error' });
    }
};

const staffDelete = async (row) => {
    Alert.confirm('Are you sure you want to delete this contact?', async () => {
        try {
            await Api.deleteStaff(row.Id, true);
            Alert.notify({ text: 'Contact deleted successfully!', type: 'success' });
            await loadStaff();
        } catch (error) {
            console.error('Error deleting staff:', error);
            Alert.notify({ text: 'Error deleting contact', type: 'error' });
        }
    });
};

const staffListDismiss = () => {
    staffListVisible.value = false;
};

const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
        Alert.notify({
            text: 'Image size must be less than 5MB',
            type: 'error'
        });
        return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
        Alert.notify({
            text: 'Please select a valid image file',
            type: 'error'
        });
        return;
    }

    try {
        const response = await ApiService.uploadProfileImage(file, 'contact', model.value.Id);
        profileImage.value = response;
        Alert.notify({
            text: 'Profile image uploaded successfully!',
            type: 'success'
        });
    } catch (error) {
        console.error('Error uploading image:', error);
        Alert.notify({
            text: 'Error uploading image',
            type: 'error'
        });
    }

    // Reset file input
    event.target.value = '';
};

const deleteImage = async () => {
    if (!profileImage.value) return;

    Alert.confirm('Are you sure you want to delete this profile image?', async () => {
        try {
            await ApiService.deleteProfileImage(profileImage.value.Id);
            profileImage.value = null;
            Alert.notify({
                text: 'Profile image deleted successfully!',
                type: 'success'
            });
        } catch (error) {
            console.error('Error deleting image:', error);
            Alert.notify({
                text: 'Error deleting image',
                type: 'error'
            });
        }
    });
};

onMounted(async () =>
{
    /*
    if (route.params.id === "new")
    {
        return;
    }
    
    model.value = await Api.loadClient(route.params.id);
    */
});

</script>